import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, KeyRound, Eye, EyeOff, Loader2, ShieldCheck, User as UserIcon, AlertCircle, X, Send, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';
import { useCustomerAuth } from '@/context/CustomerAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reusable Google logo (SVG, no external request)
const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 7.5-11.3 7.5-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C34 5.1 29.3 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.4-.1-2.5-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16 18.9 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.7C34 5.1 29.3 3 24 3 16.3 3 9.7 7.4 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 45c5.2 0 9.9-2 13.5-5.2l-6.2-5.3c-1.9 1.4-4.4 2.4-7.3 2.4-5.4 0-9.8-3-11.4-7.5l-6.6 5.1C9.6 40.6 16.3 45 24 45z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2.1-2.1 3.9-3.9 5.2l6.2 5.3C40 35.6 45 30.4 45 24c0-1.4-.1-2.5-.4-3.5z"/>
  </svg>
);

export default function CustomerAuth({ mode = 'login' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, loading, refresh } = useCustomerAuth();
  const [activeMode, setActiveMode] = useState(mode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  const next = new URLSearchParams(location.search || '').get('next') || '/account';

  useEffect(() => {
    if (!loading && customer && customer !== false) navigate(next, { replace: true });
  }, [customer, loading, navigate, next]);

  const handleGoogle = () => {
    /*
     * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
     */
    const redirectUrl = window.location.origin + '/auth/callback?next=' + encodeURIComponent(next);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Email and password are required.'); return; }
    if (activeMode === 'signup' && password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setSubmitting(true);
    try {
      const url = activeMode === 'signup' ? '/customers/auth/signup' : '/customers/auth/login';
      const body = activeMode === 'signup' ? { email, password, name } : { email, password };
      await axios.post(`${API}${url}`, body, { withCredentials: true });
      await refresh();
      navigate(next, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openForgot = () => {
    setForgotEmail(email);
    setForgotMessage('');
    setForgotOpen(true);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotSubmitting(true);
    setForgotMessage('');
    try {
      const r = await axios.post(`${API}/customers/auth/forgot-password`, {
        email: forgotEmail.trim(),
        origin_url: window.location.origin,
      });
      setForgotMessage(r.data?.message || 'If an account exists for that email, a reset link has been sent.');
    } catch {
      setForgotMessage('If an account exists for that email, a reset link has been sent.');
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-4 py-16" style={{ backgroundColor: '#f7f9f3' }} data-testid={activeMode === 'signup' ? 'customer-signup-page' : 'customer-login-page'}>
      <SEO
        title={activeMode === 'signup' ? 'Create Your Account | IIY Phytoventra' : 'Sign In to Your Account | IIY Phytoventra'}
        description={activeMode === 'signup'
          ? 'Create a free IIY Phytoventra account to track orders, earn loyalty points and check out faster.'
          : 'Sign in to your IIY Phytoventra account to view orders, loyalty rewards and manage your details.'}
        noindex
      />
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-emerald-100 overflow-hidden">
        <div className="px-8 py-6 text-white" style={{ background: 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
            <div>
              <div className="text-sm uppercase tracking-wider opacity-80">IIY Account</div>
              <div className="text-xl font-semibold">{activeMode === 'signup' ? 'Create your account' : 'Welcome back'}</div>
            </div>
          </div>
        </div>

        <div className="px-8 py-6">
          <button onClick={handleGoogle} type="button" data-testid="google-signin-button" className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 text-sm font-medium transition">
            <GoogleIcon /> Continue with Google
          </button>
          <div className="flex items-center gap-3 my-5">
            <span className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-500 uppercase tracking-wider">or use email</span>
            <span className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="customer-auth-form">
            {activeMode === 'signup' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Full name (optional)</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} disabled={submitting} placeholder="Jane Doe" className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" data-testid="customer-auth-name" />
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus disabled={submitting} placeholder="you@example.com" className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" data-testid="customer-auth-email" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-slate-700">Password</label>
                {activeMode === 'login' && (
                  <button type="button" onClick={openForgot} className="text-xs text-emerald-700 hover:underline font-medium" data-testid="customer-forgot-password-link">
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <KeyRound className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required disabled={submitting} placeholder={activeMode === 'signup' ? 'At least 8 characters' : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'} className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" data-testid="customer-auth-password" />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-2.5 p-1 text-slate-500 hover:text-slate-700" aria-label={showPw ? 'Hide password' : 'Show password'} data-testid="customer-auth-toggle-pw">{showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
            </div>

            {error && (
              <div className="text-sm rounded-lg px-3 py-2.5 bg-red-50 border border-red-200 text-red-700 flex items-start gap-2" data-testid="customer-auth-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{error}</span>
              </div>
            )}

            <button type="submit" disabled={submitting} data-testid="customer-auth-submit" className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-white font-medium transition disabled:opacity-60" style={{ background: 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)' }}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? (activeMode === 'signup' ? 'Creating account\u2026' : 'Signing in\u2026') : (activeMode === 'signup' ? 'Create account' : 'Sign in')}
            </button>

            <p className="text-xs text-center text-slate-500 pt-2">
              {activeMode === 'signup' ? (
                <>Already have an account? <button type="button" onClick={() => { setActiveMode('login'); setError(''); }} className="text-emerald-700 hover:underline font-medium" data-testid="switch-to-login">Sign in</button></>
              ) : (
                <>New to IIY? <button type="button" onClick={() => { setActiveMode('signup'); setError(''); }} className="text-emerald-700 hover:underline font-medium" data-testid="switch-to-signup">Create an account</button></>
              )}
            </p>
            <p className="text-xs text-center text-slate-500">
              <Link to="/track-order" className="text-slate-600 hover:text-emerald-700">Or track an order without an account</Link>
            </p>
          </form>
        </div>
      </motion.div>

      <AnimatePresence>
        {forgotOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center px-4"
            onClick={() => setForgotOpen(false)}
            data-testid="customer-forgot-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.96 }}
              transition={{ duration: 0.2 }} onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800">Reset your password</h3>
                <button type="button" onClick={() => setForgotOpen(false)} className="p-1 text-slate-500 hover:text-slate-700" aria-label="Close" data-testid="customer-forgot-close"><X className="w-5 h-5" /></button>
              </div>

              {forgotMessage ? (
                <div className="px-6 py-8 text-center" data-testid="customer-forgot-success">
                  <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                  <p className="text-sm text-slate-700 mb-2">{forgotMessage}</p>
                  <p className="text-xs text-slate-500 mb-5">Check your inbox (and spam folder). The link expires in 1 hour.</p>
                  <button type="button" onClick={() => setForgotOpen(false)} className="px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800" data-testid="customer-forgot-done">Done</button>
                </div>
              ) : (
                <form onSubmit={handleForgotSubmit} className="px-6 py-5 space-y-4" data-testid="customer-forgot-form">
                  <p className="text-sm text-slate-600">Enter the email you signed up with. If it matches an account, we'll email a one-time reset link valid for 1 hour.</p>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                    <input
                      type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required autoFocus disabled={forgotSubmitting}
                      placeholder="you@example.com" data-testid="customer-forgot-email-input"
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600"
                    />
                  </div>
                  <button type="submit" disabled={forgotSubmitting || !forgotEmail} data-testid="customer-forgot-submit"
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-white font-medium transition disabled:opacity-60"
                    style={{ background: 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)' }}>
                    {forgotSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {forgotSubmitting ? 'Sending\u2026' : 'Send reset link'}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
